import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { MediaItem, getMediaThumbnailUrl, isImageMediaItem } from '../../../types/MediaItem';
import { UGC_LIBRARY_MEDIA_DRAG_TYPE } from '../constants';
import {
  fetchMediaByCustomer,
  searchPexelsVideos,
  registerExternalMedia,
  type PexelsVideoResult,
} from '../../../services/contentService';
import { MediaThumbnailGrid } from '../../../components/Shared/MediaThumbnailGrid';
import { useMediaUpload } from '../../media/hooks/useMediaUpload';
import { useToast } from '../../../context/ToastContext';
import { debug } from '../../../utils/debugLogger';
import { imageGenerationUserMessage } from '../../../utils/imageGenerationMessages';

const ACCEPTED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/webp'];
const ACCEPTED_VIDEO_TYPES = ['video/mp4', 'video/webm', 'video/quicktime'];
const MAX_IMAGE_SIZE_MB = 20;
const MAX_VIDEO_SIZE_MB = 200;


export type UGCImageSourceTab = 'stock' | 'upload' | 'generate' | 'pexels';

const SOURCE_TABS = [
  { id: 'stock' as const, label: 'My Library' },
  { id: 'pexels' as const, label: 'Videos' },
  { id: 'upload' as const, label: 'Upload' },
  { id: 'generate' as const, label: 'AI Generate' },
] as const;

interface UGCAddImagesStepProps {
  /** When set (e.g. from chat agent), AI Generate is available only if this includes `generate_image`. */
  agentTools?: string[];
  customerId: string | null;
  idToken: string;
  selectedMediaIds: string[];
  onToggleMedia: (mediaId: string, mediaType?: 'image' | 'video') => void;
  onClearAllMedia: () => void;
  onMediaLoaded: (items: MediaItem[]) => void;
  /** Called when a Pexels video is imported; provides the direct video URL for timeline preview. */
  onVideoMediaImported?: (mediaId: string, videoUrl: string) => void;
  /** For selected-image strip; falls back to cloudfront from loaded items */
  thumbnailById: Record<string, string>;
  enableLibraryDrag?: boolean;
  scriptContent?: string;
  /** When set, this tab is active; hides the tab selector. */
  controlledTab?: UGCImageSourceTab;
  /** Video format — drives AI image aspect ratio (and result tile shape) so generated
   *  images fill the video frame without cropping. Defaults to reel (9:16). */
  videoFormat?: 'reel' | 'landscape' | 'square';
}

/** Map video format → image-generation aspect param and CSS tile aspect. */
const IMAGE_ASPECT_BY_FORMAT = { reel: '9x16', landscape: '16x9', square: '1x1' } as const;
/** Result tile sizing: constrain the dominant dimension so the full image stays on screen —
 *  a w-full 9:16 tile would be ~3× taller than the results area. */
const RESULT_TILE_STYLE_BY_FORMAT: Record<'reel' | 'landscape' | 'square', React.CSSProperties> = {
  reel: { aspectRatio: '9/16', height: 380 },
  landscape: { aspectRatio: '16/9', width: '100%', maxWidth: 560 },
  square: { aspectRatio: '1/1', height: 340 },
};

// ── Pexels play-icon overlay ──────────────────────────────────────────────────
const PlayIcon: React.FC = () => (
  <svg
    className="h-8 w-8 drop-shadow-lg"
    viewBox="0 0 24 24"
    fill="white"
    aria-hidden
  >
    <circle cx="12" cy="12" r="11" fill="rgba(0,0,0,0.45)" />
    <polygon points="10,8 17,12 10,16" fill="white" />
  </svg>
);

// ── Pexels video card ─────────────────────────────────────────────────────────
const PexelsVideoCard: React.FC<{
  video: PexelsVideoResult;
  isImporting: boolean;
  isSelected: boolean;
  onSelect: (video: PexelsVideoResult) => void;
  isLandscape?: boolean;
}> = ({ video, isImporting, isSelected, onSelect, isLandscape }) => (
  <button
    type="button"
    onClick={() => !isImporting && onSelect(video)}
    disabled={isImporting}
    title={`${video.photographer} · ${video.duration}s`}
    className={`group relative overflow-hidden rounded-lg border-2 bg-black transition-all focus:outline-none focus:ring-2 focus:ring-[#e4b372]/60 ${
      isSelected
        ? 'border-[#B7793B] ring-2 ring-[#B7793B]/40'
        : 'border-transparent hover:border-[#e4b372]/60'
    } ${isImporting ? 'cursor-wait opacity-70' : 'cursor-pointer'}`}
    style={{ aspectRatio: isLandscape ? '16/9' : '9/16' }}
    aria-pressed={isSelected}
  >
    <img
      src={video.thumbnail_url}
      alt={`${video.photographer} stock video`}
      className="h-full w-full object-cover transition-transform duration-200 group-hover:scale-105"
      loading="lazy"
    />
    <div className="absolute inset-0 flex items-center justify-center">
      {isImporting ? (
        <div className="h-7 w-7 animate-spin rounded-full border-2 border-white border-t-transparent drop-shadow" />
      ) : (
        <span className="opacity-80 transition-opacity group-hover:opacity-100">
          <PlayIcon />
        </span>
      )}
    </div>
    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent px-2 pb-1.5 pt-6">
      <span className="text-[11px] font-medium text-white/90 line-clamp-1">
        {video.photographer}
      </span>
      <span className="block text-[10px] text-white/60">{video.duration}s</span>
    </div>
    {isSelected && (
      <div className="absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full bg-[#B7793B] shadow">
        <svg className="h-3 w-3 text-white" fill="currentColor" viewBox="0 0 20 20">
          <path
            fillRule="evenodd"
            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
            clipRule="evenodd"
          />
        </svg>
      </div>
    )}
  </button>
);

export const UGCAddImagesStep: React.FC<UGCAddImagesStepProps> = ({
  agentTools,
  customerId,
  idToken,
  selectedMediaIds,
  onToggleMedia,
  onClearAllMedia,
  onMediaLoaded,
  onVideoMediaImported,
  thumbnailById,
  enableLibraryDrag = false,
  scriptContent = '',
  controlledTab,
  videoFormat = 'reel',
}) => {
  const [imageTab, setImageTab] = useState<UGCImageSourceTab>('stock');
  const activeTab = controlledTab ?? imageTab;
  const [searchQuery, setSearchQuery] = useState('');
  const [mediaItems, setMediaItems] = useState<MediaItem[]>([]);
  // External items (Pexels-imported) merged into the library for thumbnail resolution
  const [externalItems, setExternalItems] = useState<MediaItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { showToast } = useToast();

  const [generatePrompt, setGeneratePrompt] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [generateError, setGenerateError] = useState<string | null>(null);
  const [generatedImages, setGeneratedImages] = useState<{ image_url: string; media_id: string }[]>([]);
  const generateResultsRef = useRef<HTMLDivElement>(null);
  // The prompt form above is tall, so the result lands below the fold — scroll it into view
  // as soon as generation starts (spinner) and again when the image arrives.
  useEffect(() => {
    if (isGenerating || generatedImages.length > 0) {
      generateResultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [isGenerating, generatedImages.length]);

  const PAGE_SIZE = 20;
  const [libraryPage, setLibraryPage] = useState(0);
  const [libraryTypeFilter, setLibraryTypeFilter] = useState<'all' | 'images' | 'videos'>('all');
  const canUseAiImageGeneration =
    agentTools === undefined || agentTools.includes('generate_image');

  // ── Pexels state ──────────────────────────────────────────────────────────
  const [pexelsQuery, setPexelsQuery] = useState('');
  const [pexelsResults, setPexelsResults] = useState<PexelsVideoResult[]>([]);
  const [pexelsLoading, setPexelsLoading] = useState(false);
  const [pexelsError, setPexelsError] = useState<string | null>(null);
  const [pexelsSearched, setPexelsSearched] = useState(false);
  const [pexelsOrientation, setPexelsOrientation] = useState<'portrait' | 'landscape'>('portrait');
  /** media_id of videos currently being imported (show per-card spinner) */
  const [importingIds, setImportingIds] = useState<Set<string>>(new Set());
  const [importError, setImportError] = useState<string | null>(null);

  const sourceTabs = SOURCE_TABS.filter(
    (t) => t.id !== 'generate' || canUseAiImageGeneration
  );

  useEffect(() => {
    if (!canUseAiImageGeneration && imageTab === 'generate') {
      setImageTab('stock');
    }
  }, [canUseAiImageGeneration, imageTab]);

  const onMediaLoadedRef = useRef(onMediaLoaded);
  onMediaLoadedRef.current = onMediaLoaded;

  const loadMedia = useCallback(async () => {
    if (!customerId || !idToken) {
      setMediaItems([]);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { medias } = await fetchMediaByCustomer(customerId, idToken, { limit: 50 });
      const visual = medias.filter((item: MediaItem) => {
        const t = (item.media_type || '').trim().toLowerCase();
        return t !== 'audio' && (isImageMediaItem(item) || t === 'video');
      });
      setMediaItems(visual);
      onMediaLoadedRef.current?.([...visual, ...externalItems]);
    } catch (err: unknown) {
      debug.error('Failed to fetch media:', err);
      setError(err instanceof Error ? err.message : 'Failed to load media');
    } finally {
      setLoading(false);
    }
  }, [customerId, idToken, externalItems]);

  const { upload, isUploading, uploadError, resetUpload } = useMediaUpload();

  useEffect(() => {
    loadMedia();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId, idToken]);

  // Reset to page 0 whenever the search query, tab, or type filter changes
  useEffect(() => { setLibraryPage(0); }, [searchQuery, imageTab, libraryTypeFilter]);

  const filteredItems = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return mediaItems;
    return mediaItems.filter((item) => {
      const tagStr = (item.tags || []).join(' ').toLowerCase();
      return tagStr.includes(q) || item.s3_key.toLowerCase().includes(q) || item.media_id.toLowerCase().includes(q);
    });
  }, [mediaItems, searchQuery]);

  const typeFilteredItems = useMemo(() => {
    if (libraryTypeFilter === 'images') return filteredItems.filter((m) => (m.media_type || '').trim().toLowerCase() !== 'video');
    if (libraryTypeFilter === 'videos') return filteredItems.filter((m) => (m.media_type || '').trim().toLowerCase() === 'video');
    return filteredItems;
  }, [filteredItems, libraryTypeFilter]);

  const handleUploadClick = () => {
    resetUpload();
    fileInputRef.current?.click();
  };

  const processFiles = useCallback(async (rawFiles: File[]) => {
    if (!customerId || !idToken) {
      showToast('Please sign in to upload files.', 'error');
      return;
    }

    const valid: File[] = [];
    for (const file of rawFiles) {
      const isImg = ACCEPTED_IMAGE_TYPES.includes(file.type);
      const isVid = ACCEPTED_VIDEO_TYPES.includes(file.type);
      if (!isImg && !isVid) {
        showToast(`"${file.name}" is not supported. Use PNG, JPG, WebP, MP4, WebM, or MOV.`, 'warning');
        continue;
      }
      const limitMB = isVid ? MAX_VIDEO_SIZE_MB : MAX_IMAGE_SIZE_MB;
      if (file.size > limitMB * 1024 * 1024) {
        showToast(`"${file.name}" exceeds the ${limitMB} MB limit.`, 'warning');
        continue;
      }
      valid.push(file);
    }

    if (valid.length === 0) return;

    let ok = 0;
    let fail = 0;
    for (const file of valid) {
      const result = await upload({ file, customerId, idToken, mediaType: 'processed' });
      result.success ? ok++ : fail++;
    }

    await loadMedia();
    resetUpload();

    if (ok > 0 && fail === 0) {
      showToast(ok === 1 ? 'File uploaded successfully.' : `${ok} files uploaded successfully.`, 'success');
      setImageTab('stock');
    } else if (ok > 0) {
      showToast(`${ok} uploaded, ${fail} failed. Please retry the failed files.`, 'warning');
      setImageTab('stock');
    } else {
      showToast(uploadError || 'Upload failed. Please try again.', 'error');
    }
  }, [customerId, idToken, upload, loadMedia, resetUpload, showToast, uploadError]);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (files.length > 0) await processFiles(files);
  };

  const handleDropzoneDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    if (e.dataTransfer.types.includes('Files')) {
      setIsDraggingOver(true);
      e.dataTransfer.dropEffect = 'copy';
    }
  };

  const handleDropzoneDragLeave = (e: React.DragEvent) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setIsDraggingOver(false);
    }
  };

  const handleDropzoneDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDraggingOver(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) await processFiles(files);
  };

  const resolveThumb = (id: string) => {
    const fromMap = thumbnailById[id];
    if (fromMap) return fromMap;
    const item = mediaItems.find((m) => m.media_id === id) ?? externalItems.find((m) => m.media_id === id);
    return item ? getMediaThumbnailUrl(item) : undefined;
  };

  // ── Pexels handlers ───────────────────────────────────────────────────────
  const handlePexelsSearch = async (orientationOverride?: 'portrait' | 'landscape') => {
    const q = pexelsQuery.trim();
    if (!q) return;
    const orientation = orientationOverride ?? pexelsOrientation;
    setPexelsLoading(true);
    setPexelsError(null);
    setPexelsResults([]);
    try {
      const result = await searchPexelsVideos(q, idToken, { perPage: 20, orientation });
      setPexelsResults(result.videos);
      setPexelsSearched(true);
      if (result.videos.length === 0) {
        setPexelsError('No videos found for that search. Try different keywords.');
      }
    } catch (err: unknown) {
      setPexelsError(err instanceof Error ? err.message : 'Search failed. Please try again.');
    } finally {
      setPexelsLoading(false);
    }
  };

  const handleOrientationChange = (orientation: 'portrait' | 'landscape') => {
    setPexelsOrientation(orientation);
    if (pexelsSearched && pexelsQuery.trim()) {
      handlePexelsSearch(orientation);
    }
  };

  const handlePexelsVideoSelect = async (video: PexelsVideoResult) => {
    if (!customerId || !idToken) return;
    setImportError(null);
    setImportingIds((prev) => new Set(prev).add(video.id));

    try {
      const { media_id } = await registerExternalMedia(
        {
          customerId,
          mediaType: 'VIDEO',
          externalUrl: video.video_url,
          s3Key: `external/pexels/${video.id}.mp4`,
          tags: ['pexels', 'video', 'stock'],
          metadata: {
            source: 'pexels',
            pexels_id: video.id,
            duration: video.duration,
            width: video.width,
            height: video.height,
            photographer: video.photographer,
          },
        },
        idToken
      );

      // Build a synthetic MediaItem so the thumbnail strip resolves correctly
      const syntheticItem: MediaItem = {
        media_id,
        media_type: 'VIDEO',
        cloudfront_url: video.thumbnail_url,
        s3_key: `external/pexels/${video.id}.mp4`,
        tags: ['pexels', 'video', 'stock'],
        processed: true,
        created_at: new Date().toISOString(),
        metadata: { size: 0, dimensions: `${video.width}x${video.height}` },
      };

      setExternalItems((prev) => {
        const next = [...prev.filter((x) => x.media_id !== media_id), syntheticItem];
        onMediaLoadedRef.current?.([...mediaItems, ...next]);
        return next;
      });

      onToggleMedia(media_id, 'video');
      onVideoMediaImported?.(media_id, video.video_url);
    } catch (err: unknown) {
      setImportError(err instanceof Error ? err.message : 'Failed to add video. Please try again.');
    } finally {
      setImportingIds((prev) => {
        const next = new Set(prev);
        next.delete(video.id);
        return next;
      });
    }
  };

  // ─────────────────────────────────────────────────────────────────────────

  return (
    <div
      className="flex min-h-0 flex-1 flex-col gap-2"
    >
      {/* Source switcher — dropdown (hidden when controlled from parent) */}
      {!controlledTab && (
        <div className="relative">
          <select
            value={imageTab}
            onChange={(e) => setImageTab(e.target.value as UGCImageSourceTab)}
            className="w-full appearance-none rounded-lg border border-[#E5E5E5] bg-white py-2 pl-3 pr-8 text-xs font-semibold text-[#374151] outline-none focus:border-[#e4b372] focus:ring-2 focus:ring-[#e4b372]/25 cursor-pointer"
            aria-label="Image source"
          >
            {sourceTabs.map((t) => (
              <option key={t.id} value={t.id}>{t.label}</option>
            ))}
          </select>
          <svg className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      )}

      {selectedMediaIds.length > 0 && (
        <div className="flex items-center gap-2 rounded-lg border border-[#E5E5E5] bg-white px-2.5 py-1.5">
          <span className="flex-shrink-0 rounded-full bg-[#e4b372]/15 px-2 py-0.5 text-[10px] font-bold text-[#92400e]">
            {selectedMediaIds.length}
          </span>
          <div className="flex min-w-0 flex-1 gap-1.5 overflow-x-auto [scrollbar-width:none]">
            {selectedMediaIds.map((id) => {
              const url = resolveThumb(id);
              return url
                ? <img key={id} src={url} alt="" className="h-8 w-8 flex-shrink-0 rounded border border-[#E5E5E5] object-cover" />
                : <div key={id} className="h-8 w-8 flex-shrink-0 animate-pulse rounded border border-[#E5E5E5] bg-[#F3F4F6]" />;
            })}
          </div>
          <button
            type="button"
            onClick={onClearAllMedia}
            className="flex-shrink-0 text-[10px] text-[#94a3b8] hover:text-red-500 transition-colors"
          >
            Clear
          </button>
        </div>
      )}

      <input
        type="file"
        ref={fileInputRef}
        accept="image/*,video/*"
        multiple
        className="hidden"
        onChange={handleFileChange}
        disabled={isUploading}
        aria-hidden
      />

      {/* ── STOCK LIBRARY ── */}
      {activeTab === 'stock' && (
        <div className="flex min-h-0 flex-1 flex-col gap-2">
          {(!customerId || !idToken) && (
            <div
              className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950"
              role="status"
            >
              Sign in with a connected account to load your media library, or use the Upload tab to add images from
              this device.
            </div>
          )}
          <div className="flex gap-2">
            <input
              id="ugc-image-search"
              type="search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search library…"
              className="min-w-0 flex-1 rounded-lg border border-[#E5E5E5] bg-white px-3 py-2 text-xs text-[#1F2937] placeholder:text-[#9CA3AF] focus:border-[#e4b372] focus:outline-none focus:ring-2 focus:ring-[#e4b372]/25"
            />
            <div className="relative flex-shrink-0">
              <select
                value={libraryTypeFilter}
                onChange={(e) => setLibraryTypeFilter(e.target.value as 'all' | 'images' | 'videos')}
                className="h-full appearance-none rounded-lg border border-[#E5E5E5] bg-white py-2 pl-3 pr-7 text-xs font-semibold text-[#374151] outline-none focus:border-[#e4b372] focus:ring-2 focus:ring-[#e4b372]/25 cursor-pointer"
                aria-label="Filter by media type"
              >
                <option value="all">All</option>
                <option value="images">Images</option>
                <option value="videos">Videos</option>
              </select>
              <svg className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 h-3 w-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>

          <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border border-[#E5E5E5] bg-white shadow-sm">
            {loading ? (
              <div className="flex min-h-[220px] flex-1 flex-col items-center justify-center gap-3 py-12">
                <div className="h-9 w-9 animate-spin rounded-full border-2 border-[#e4b372] border-t-transparent" />
                <span className="text-sm text-[#64748b]">Loading your library…</span>
              </div>
            ) : error ? (
              <div className="flex min-h-[240px] flex-1 flex-col items-center justify-center gap-4 px-6 py-10 text-center">
                <p className="max-w-md text-sm font-medium text-red-700">
                  Library could not be loaded ({error}). You can still add images using{" "}
                  <span className="whitespace-nowrap font-semibold">Upload from computer</span> above.
                </p>
                <div className="flex flex-wrap items-center justify-center gap-2">
                  <button
                    type="button"
                    onClick={() => loadMedia()}
                    className="rounded-lg border border-red-200 bg-white px-4 py-2 text-sm font-semibold text-red-800 transition-colors hover:bg-red-50"
                  >
                    Retry library
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setImageTab("upload");
                      handleUploadClick();
                    }}
                    className="rounded-lg bg-[#e4b372] px-4 py-2 text-sm font-semibold text-white shadow-sm hover:brightness-95"
                  >
                    Upload instead
                  </button>
                </div>
              </div>
            ) : typeFilteredItems.length === 0 ? (
              <div className="m-4 flex min-h-[220px] flex-1 flex-col items-center justify-center rounded-lg border border-dashed border-[#E5E5E5] bg-[#FAFAFA] px-6 py-12 text-center">
                <p className="text-sm font-medium text-[#475569]">
                  {mediaItems.length === 0
                    ? "No images in your library yet."
                    : libraryTypeFilter !== 'all'
                    ? `No ${libraryTypeFilter} match your search.`
                    : "Nothing matches your search."}
                </p>
                <p className="mt-2 max-w-sm text-xs text-[#64748b]">
                  {mediaItems.length === 0
                    ? "Upload images from your computer, or search Pexels Videos for free stock scenes."
                    : libraryTypeFilter !== 'all'
                    ? "Try changing the filter to All, or clear the search."
                    : "Try different keywords or clear the search box — or upload new images."}
                </p>
                <div className="mt-5 flex flex-wrap justify-center gap-2">
                  {libraryTypeFilter !== 'all' && (
                    <button
                      type="button"
                      onClick={() => setLibraryTypeFilter('all')}
                      className="rounded-lg border border-[#E5E5E5] bg-white px-4 py-2 text-sm font-semibold text-[#374151] hover:bg-slate-50"
                    >
                      Show all
                    </button>
                  )}
                  {mediaItems.length > 0 && searchQuery && (
                    <button
                      type="button"
                      onClick={() => setSearchQuery("")}
                      className="rounded-lg border border-[#E5E5E5] bg-white px-4 py-2 text-sm font-semibold text-[#374151] hover:bg-slate-50"
                    >
                      Clear search
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={handleUploadClick}
                    disabled={!customerId || !idToken || isUploading}
                    className="rounded-lg bg-[#e4b372] px-4 py-2 text-sm font-semibold text-white shadow-sm hover:brightness-95 disabled:opacity-50"
                  >
                    Upload images
                  </button>
                  <button
                    type="button"
                    onClick={() => setImageTab('pexels')}
                    className="rounded-lg border border-[#e4b372]/40 bg-white px-4 py-2 text-sm font-semibold text-[#92400e] hover:bg-[#fffbeb]"
                  >
                    Browse Pexels Videos
                  </button>
                </div>
              </div>
            ) : (() => {
              const imageItems = typeFilteredItems.filter((m) => (m.media_type || '').trim().toLowerCase() !== 'video');
              const videoItems = typeFilteredItems.filter((m) => (m.media_type || '').trim().toLowerCase() === 'video');
              const totalPages = Math.ceil(imageItems.length / PAGE_SIZE);
              const pageImageItems = imageItems.slice(libraryPage * PAGE_SIZE, (libraryPage + 1) * PAGE_SIZE);
              return (
                <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
                  {/* Images section */}
                  {imageItems.length > 0 && (
                    <div className="p-2">
                      <p className="mb-1.5 px-0.5 text-[10px] font-semibold uppercase tracking-wider text-gray-400">Images</p>
                      <MediaThumbnailGrid
                        items={pageImageItems}
                        selectedIds={selectedMediaIds}
                        onToggle={(id) => onToggleMedia(id, 'image')}
                        columns={3}
                        size="sm"
                        fillAvailable={false}
                        maxHeight="max-h-none"
                        matchAvatarStyle={false}
                        horizontalScroll={false}
                        tealSelection
                        animateEntry={false}
                        enableLibraryDrag={enableLibraryDrag}
                        libraryDragMimeType={UGC_LIBRARY_MEDIA_DRAG_TYPE}
                        thumbnailAspectRatio="9/16"
                      />
                    </div>
                  )}
                  {/* Videos section */}
                  {videoItems.length > 0 && (
                    <div className="border-t border-gray-100 p-2">
                      <p className="mb-1.5 px-0.5 text-[10px] font-semibold uppercase tracking-wider text-brand-primary">Videos</p>
                      <div className="grid grid-cols-3 gap-3">
                        {videoItems.map((item) => {
                          const isSelected = selectedMediaIds.includes(item.media_id);
                          const src = item.cloudfront_url;
                          return (
                            <button
                              key={item.media_id}
                              type="button"
                              onClick={() => { onToggleMedia(item.media_id, 'video'); const url = item.cloudfront_url; if (url && /\.(mp4|webm|mov|m4v|avi|mkv)(\?|#|$)/i.test(url)) onVideoMediaImported?.(item.media_id, url); }}
                              className={`relative overflow-hidden rounded-lg border-2 bg-black transition-all focus:outline-none ${isSelected ? 'border-teal-500 ring-2 ring-teal-100' : 'border-transparent hover:border-teal-500'}`}
                              style={{ aspectRatio: '9/16' }}
                            >
                              {src ? (
                                <video
                                  src={src}
                                  className="h-full w-full object-cover"
                                  muted
                                  playsInline
                                  preload="metadata"
                                />
                              ) : (
                                <div className="flex h-full w-full items-center justify-center bg-gray-900">
                                  <svg className="h-6 w-6 text-gray-500" fill="currentColor" viewBox="0 0 24 24"><path d="M4 6a2 2 0 012-2h8a2 2 0 012 2v4l4-2v8l-4-2v4a2 2 0 01-2 2H6a2 2 0 01-2-2V6z" /></svg>
                                </div>
                              )}
                              <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                                <svg className="h-7 w-7 drop-shadow-lg" viewBox="0 0 24 24" fill="white" aria-hidden>
                                  <circle cx="12" cy="12" r="11" fill="rgba(0,0,0,0.45)" />
                                  <polygon points="10,8 17,12 10,16" fill="white" />
                                </svg>
                              </div>
                              {isSelected && (
                                <div className="absolute top-1 left-1 flex h-4 w-4 items-center justify-center rounded-full bg-teal-500 border-2 border-white">
                                  <svg className="h-2.5 w-2.5 text-white" fill="none" stroke="currentColor" strokeWidth={3} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                                </div>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  {imageItems.length === 0 && videoItems.length === 0 && null}
                  {totalPages > 1 && (
                    <div className="flex flex-shrink-0 items-center justify-between border-t border-gray-100 px-3 py-2">
                      <button
                        disabled={libraryPage === 0}
                        onClick={() => setLibraryPage(p => p - 1)}
                        className="rounded px-2.5 py-1 text-[11px] font-medium text-gray-600 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                      >
                        ← Prev
                      </button>
                      <span className="text-[10px] text-gray-400">
                        {libraryPage + 1} / {totalPages} · {imageItems.length} {imageItems.length === 1 ? 'image' : 'images'}
                      </span>
                      <button
                        disabled={libraryPage >= totalPages - 1}
                        onClick={() => setLibraryPage(p => p + 1)}
                        className="rounded px-2.5 py-1 text-[11px] font-medium text-gray-600 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                      >
                        Next →
                      </button>
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {/* ── PEXELS VIDEOS ── */}
      {activeTab === 'pexels' && (
        <div className="flex min-h-0 flex-1 flex-col gap-2">
          {importError && (
            <div
              className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900"
              role="alert"
            >
              {importError}
            </div>
          )}

          {/* Orientation filter */}
          <div className="flex gap-1.5">
            <button
              type="button"
              onClick={() => handleOrientationChange('portrait')}
              className={`flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-[11px] font-medium transition-all ${pexelsOrientation === 'portrait' ? 'border-[#e4b372] bg-[#e4b372]/10 text-[#b8832a]' : 'border-[#E5E5E5] bg-white text-gray-500 hover:bg-gray-50'}`}
            >
              <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><rect x="7" y="2" width="10" height="20" rx="2" /></svg>
              Portrait
            </button>
            <button
              type="button"
              onClick={() => handleOrientationChange('landscape')}
              className={`flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-[11px] font-medium transition-all ${pexelsOrientation === 'landscape' ? 'border-[#e4b372] bg-[#e4b372]/10 text-[#b8832a]' : 'border-[#E5E5E5] bg-white text-gray-500 hover:bg-gray-50'}`}
            >
              <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><rect x="2" y="7" width="20" height="10" rx="2" /></svg>
              Landscape
            </button>
          </div>

          {/* Search bar */}
          <div className="flex gap-2">
            <input
              id="pexels-video-search"
              type="search"
              value={pexelsQuery}
              onChange={(e) => setPexelsQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handlePexelsSearch()}
              placeholder="Search Pexels videos…"
              className="flex-1 rounded-lg border border-[#E5E5E5] bg-white px-3 py-2 text-xs text-[#1F2937] placeholder:text-[#9CA3AF] focus:border-[#e4b372] focus:outline-none focus:ring-2 focus:ring-[#e4b372]/25"
              disabled={pexelsLoading}
            />
            <button
              type="button"
              onClick={() => handlePexelsSearch()}
              disabled={pexelsLoading || !pexelsQuery.trim()}
              className="flex items-center gap-1.5 rounded-lg bg-[#e4b372] px-3 py-2 text-xs font-semibold text-white transition-all hover:brightness-95 disabled:opacity-50"
            >
              {pexelsLoading
                ? <div className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white border-t-transparent" />
                : <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 11A6 6 0 105 11a6 6 0 0012 0z" /></svg>}
              Search
            </button>
          </div>

          {/* Results grid */}
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border border-[#E5E5E5] bg-white shadow-sm">
            {pexelsLoading ? (
              <div className="flex flex-1 flex-col items-center justify-center gap-3 py-12">
                <div className="h-9 w-9 animate-spin rounded-full border-2 border-[#e4b372] border-t-transparent" />
                <span className="text-sm text-[#64748b]">Searching Pexels…</span>
              </div>
            ) : pexelsError ? (
              <div className="flex flex-1 flex-col items-center justify-center px-6 py-10 text-center">
                <p className="max-w-md text-sm text-[#64748b]">{pexelsError}</p>
              </div>
            ) : !pexelsSearched ? (
              <div className="flex flex-1 flex-col items-center justify-center gap-3 px-6 py-10 text-center">
                <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#f1f5f9] text-[#94a3b8]">
                  <svg className="h-7 w-7" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 10.5l4.72-4.72a.75.75 0 011.28.53v11.38a.75.75 0 01-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 002.25-2.25v-9a2.25 2.25 0 00-2.25-2.25h-9A2.25 2.25 0 002.25 7.5v9a2.25 2.25 0 002.25 2.25z" />
                  </svg>
                </div>
                <p className="text-sm font-medium text-[#475569]">Search for free stock videos</p>
                <p className="max-w-xs text-xs text-[#64748b]">
                  Select {pexelsOrientation} orientation above, type keywords, and press Search to find free stock videos.
                </p>
              </div>
            ) : (
              <div className="flex-1 min-h-0 overflow-y-auto p-3">
                <div className={`grid gap-2 ${pexelsOrientation === 'landscape' ? 'grid-cols-3' : 'grid-cols-4'}`}>
                  {pexelsResults.map((video) => (
                    <PexelsVideoCard
                      key={video.id}
                      video={video}
                      isImporting={importingIds.has(video.id)}
                      isSelected={false}
                      onSelect={handlePexelsVideoSelect}
                      isLandscape={pexelsOrientation === 'landscape'}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── UPLOAD ── */}
      {activeTab === 'upload' && (
        <div
          className={`flex min-h-[220px] flex-col items-center justify-center rounded-2xl border-2 border-dashed px-6 py-12 transition-colors ${
            isDraggingOver
              ? 'border-[#e4b372] bg-[#fffbeb]'
              : 'border-[#E5E5E5] bg-gradient-to-b from-white to-[#FAFAFA]'
          }`}
          onDragOver={handleDropzoneDragOver}
          onDragLeave={handleDropzoneDragLeave}
          onDrop={handleDropzoneDrop}
        >
          {isUploading ? (
            <>
              <div className="mb-4 h-11 w-11 animate-spin rounded-full border-2 border-[#e4b372] border-t-transparent" />
              <p className="text-sm font-medium text-[#64748b]">Uploading…</p>
            </>
          ) : isDraggingOver ? (
            <>
              <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-[#e4b372]/15 text-[#b8832a]">
                <svg className="h-7 w-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 7.5m0 0L7.5 12M12 7.5v9" />
                </svg>
              </div>
              <p className="text-base font-semibold text-[#b8832a]">Drop to upload</p>
            </>
          ) : (
            <>
              <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-[#f1f5f9] text-[#94a3b8]">
                <svg className="h-7 w-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M12 3v13.5m0 0l4.5-4.5M12 16.5l-4.5-4.5"
                  />
                </svg>
              </div>
              <p className="text-base font-semibold text-[#1e293b]">Upload to your library</p>
              <p className="mt-1 max-w-md text-center text-sm text-[#64748b]">
                Drag & drop here, or choose files below.
              </p>
              <p className="mt-1 text-xs text-[#94a3b8]">
                PNG, JPG, WebP up to {MAX_IMAGE_SIZE_MB} MB · MP4, WebM, MOV up to {MAX_VIDEO_SIZE_MB} MB
              </p>
              <button
                type="button"
                onClick={handleUploadClick}
                className="mt-6 rounded-xl bg-[#e4b372] px-6 py-2.5 text-sm font-semibold text-white shadow-sm transition-all hover:brightness-95"
              >
                Choose files
              </button>
            </>
          )}
        </div>
      )}

      {/* ── AI GENERATE ── */}
      {activeTab === 'generate' && (
        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto rounded-2xl border border-[#E5E5E5] bg-white shadow-sm">

          {/* Prompt area */}
          <div className="flex-shrink-0 border-b border-[#E5E5E5] p-4">
            {scriptContent && (
              <div className="mb-3 rounded-lg bg-[#F9FAFB] p-2.5 border border-[#E5E5E5]">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-[#9CA3AF] mb-1">Script reference</p>
                <div
                  className="max-h-24 overflow-y-auto text-xs text-[#374151] leading-relaxed whitespace-pre-wrap break-words"
                  tabIndex={0}
                  role="region"
                  aria-label="Full script text"
                >
                  {scriptContent}
                </div>
              </div>
            )}
            <p className="mb-2 text-xs text-[#57534e]">
              <span className="font-semibold text-[#92400e]">Describe one scene:</span>{' '}
              who is in it, what they're doing, and where. Don't paste the full script.
            </p>
            <textarea
              value={generatePrompt}
              onChange={(e) => { setGeneratePrompt(e.target.value); setGeneratedImages([]); }}
              placeholder="E.g. business owner writing on her phone, cozy café, warm light, realistic"
              rows={3}
              className="w-full resize-none rounded-lg border border-[#E5E5E5] bg-[#FAFAFA] p-3 text-sm text-[#1F2937] placeholder:text-[#9CA3AF] focus:border-[#e4b372] focus:outline-none focus:ring-2 focus:ring-[#e4b372]/25"
              aria-describedby="ugc-image-prompt-hint"
            />
            <p id="ugc-image-prompt-hint" className="sr-only">Describe one picture in plain language.</p>
            {generateError && (
              <p className="mt-1.5 text-xs text-red-600">{generateError}</p>
            )}
            <div className="mt-3 flex justify-end">
              <button
                onClick={async () => {
                  if (!canUseAiImageGeneration || !customerId) return;
                  const trimmed = generatePrompt.trim();
                  if (!trimmed) return;
                  if (trimmed.length < 12) {
                    setGenerateError("Please write a clearer description — at least a short sentence.");
                    return;
                  }
                  setIsGenerating(true);
                  setGenerateError(null);
                  setGeneratedImages([]);
                  try {
                    const { generateImage } = await import('../../../services/contentService');
                    const response = await generateImage(
                      {
                        prompt: trimmed,
                        customer_id: customerId,
                        provider: "gemini",
                        num_images: 1,
                        // Match the video's format so the image fills the frame uncropped.
                        aspect_ratio: IMAGE_ASPECT_BY_FORMAT[videoFormat],
                      },
                      idToken,
                      (img) => {
                        setGeneratedImages((prev) => [...prev, { image_url: img.image_url, media_id: img.media_id }]);
                      }
                    );
                    if (response.success) {
                      setGeneratePrompt("");
                      setGeneratedImages(response.images.map((i) => ({ image_url: i.image_url, media_id: i.media_id })));
                      await loadMedia();
                    } else {
                      setGenerateError(imageGenerationUserMessage(response.error || "Failed to generate image."));
                    }
                  } catch (err: any) {
                    setGenerateError(imageGenerationUserMessage(err.message || "An unexpected error occurred."));
                  } finally {
                    setIsGenerating(false);
                  }
                }}
                disabled={isGenerating || !generatePrompt.trim() || !customerId}
                className="flex items-center gap-2 rounded-lg bg-[#e4b372] px-5 py-2 text-sm font-semibold text-white shadow-sm transition-all hover:brightness-95 disabled:opacity-50"
              >
                {isGenerating ? (
                  <>
                    <div className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white border-t-transparent" />
                    Generating…
                  </>
                ) : (
                  <>
                    <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" />
                    </svg>
                    Generate
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Results area — flows under the form; the whole tab column scrolls */}
          <div ref={generateResultsRef} className="flex flex-col">
            {isGenerating && generatedImages.length === 0 ? (
              <div className="flex flex-1 flex-col items-center justify-center gap-3 p-8">
                <div className="relative flex h-16 w-16 items-center justify-center">
                  <div className="absolute inset-0 animate-ping rounded-full bg-[#e4b372]/20" />
                  <div className="h-8 w-8 animate-spin rounded-full border-2 border-[#e4b372] border-t-transparent" />
                </div>
                <p className="text-sm font-medium text-[#92400e]">Creating your image…</p>
                <p className="text-xs text-[#94a3b8]">Usually takes 10–20 seconds</p>
              </div>
            ) : generatedImages.length > 0 ? (
              <div className="p-4">
                <p className="mb-3 text-[10px] font-semibold uppercase tracking-wider text-[#9CA3AF]">
                  Result · click to add to timeline
                </p>
                <div className="flex flex-col gap-3">
                  {generatedImages.map((img) => {
                    const isSelected = selectedMediaIds.includes(img.media_id);
                    return (
                      <div key={img.media_id} className="flex flex-col items-center gap-2">
                        <button
                          type="button"
                          onClick={() => onToggleMedia(img.media_id, 'image')}
                          className={`group relative overflow-hidden rounded-xl border-2 bg-[#f1f5f9] transition-all focus:outline-none focus:ring-2 focus:ring-[#e4b372]/50 ${
                            isSelected
                              ? 'border-teal-500 ring-2 ring-teal-100'
                              : 'border-transparent hover:border-[#e4b372]'
                          }`}
                          style={RESULT_TILE_STYLE_BY_FORMAT[videoFormat]}
                          aria-pressed={isSelected}
                        >
                          <img
                            src={img.image_url}
                            alt="AI generated"
                            className="h-full w-full object-cover transition-transform duration-200 group-hover:scale-105"
                          />
                          {isSelected ? (
                            <div className="absolute inset-0 flex items-end justify-start p-2">
                              <span className="flex items-center gap-1 rounded-full bg-teal-500 px-2.5 py-1 text-[11px] font-semibold text-white shadow">
                                <svg className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth={3} viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                </svg>
                                Added to timeline
                              </span>
                            </div>
                          ) : (
                            <div className="absolute inset-0 flex items-center justify-center bg-black/0 transition-colors group-hover:bg-black/10">
                              <span className="rounded-full bg-white/90 px-4 py-1.5 text-xs font-semibold text-[#1e293b] opacity-0 shadow transition-opacity group-hover:opacity-100">
                                + Add to timeline
                              </span>
                            </div>
                          )}
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="flex flex-1 flex-col items-center justify-center gap-2 p-8 text-center">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#f1f5f9]">
                  <svg className="h-6 w-6 text-[#94a3b8]" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" />
                  </svg>
                </div>
                <p className="text-sm font-medium text-[#475569]">Your image will appear here</p>
                <p className="text-xs text-[#94a3b8]">Write a scene description above and hit Generate</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
